/**
 * Unsaved-edit detection for the modal dialogs with an explicit Save (HS2-FSBQVX).
 * A guarded dialog carries `data-unsaved-guard="<what it edits>"`. Each form
 * control is recorded with the value it rendered with when it entered the
 * document; the dialog is dirty while any control's live value differs from that
 * record. Recording at mount (not reading the `value` attribute later) keeps the
 * baseline fixed for controls whose rendered value follows live state, such as the
 * decision dialog's quote select. A minimal DOM surface, so it is unit-testable
 * without a browser.
 */

/** The form controls whose edits count; a hidden native input never does. */
export const GUARDED_CONTROLS = 'input:not([type="hidden"]), textarea, kui-select, wa-switch, wa-radio-group';
export const GUARDED_DIALOG = 'dialog[data-unsaved-guard]';
/** Marks a dialog's Cancel button and its Save button, which the prompt's Save presses. */
export const GUARD_CANCEL = '[data-unsaved-cancel]';
export const GUARD_SAVE = '[data-unsaved-save]';

/** The slice of a form control the guard reads. */
export interface GuardedControl {
  readonly localName: string;
  value?: unknown;
  checked?: boolean;
  type?: string;
  getAttribute(name: string): string | null;
  hasAttribute(name: string): boolean;
  querySelector(selector: string): { getAttribute(name: string): string | null } | null;
  closest?(selector: string): Element | null;
  focus?(): void;
}

export interface GuardedDialogRoot {
  querySelectorAll(selector: string): Iterable<GuardedControl>;
}

const isToggle = (control: GuardedControl): boolean =>
  (control.localName === 'input' && control.type === 'checkbox') || control.localName === 'wa-switch';

const text = (value: unknown): string =>
  Array.isArray(value) ? value.map(String).join('\u0000') : typeof value === 'string' ? value.trim() : '';

/** The value a control rendered with: its `value` / `checked` attribute, or a select's selected option. */
export function renderedValue(control: GuardedControl): string {
  if (isToggle(control)) return control.hasAttribute('checked') ? 'on' : '';
  const value = control.getAttribute('value');
  if (value !== null) return value.trim();
  // Native textarea renders its initial value as text content, not a value attribute.
  if (control.localName === 'textarea') return text(control.value);
  // Kerf's Select (beta 106) marks the rendered choice on its listbox option.
  if (control.localName === 'kui-select')
    return control.querySelector('.kui-select__option[aria-selected="true"]')?.getAttribute('data-value') ?? '';
  return '';
}

/** A control's live value. */
export function liveValue(control: GuardedControl): string {
  if (isToggle(control)) return control.checked === true ? 'on' : '';
  return text(control.value);
}

/** Equal as entered: number fields compare by value ("5" = "5.0"), everything else as trimmed text. */
function sameValue(control: GuardedControl, saved: string, live: string): boolean {
  if (control.type === 'number' && saved !== '' && live !== '') {
    const a = Number(saved);
    const b = Number(live);
    if (Number.isFinite(a) && Number.isFinite(b)) return a === b;
  }
  return saved === live;
}

const initialValues = new WeakMap<object, string>();

/** Record each control's rendered value the first time it is seen; later sightings keep the first record. */
export function recordInitialValues(controls: Iterable<GuardedControl>): void {
  for (const control of controls) {
    if (!initialValues.has(control)) initialValues.set(control, renderedValue(control));
  }
}

/** The controls in a dialog whose live value differs from the value they mounted with. */
export function changedDialogControls(dialog: GuardedDialogRoot): GuardedControl[] {
  const changed: GuardedControl[] = [];
  for (const control of dialog.querySelectorAll(GUARDED_CONTROLS)) {
    // Controls that save on their own, like a notes composer inside the line dialog, opt out with
    // `data-unsaved-ignore`: the dialog's Save would not save them (HS2-JRQT8A).
    if (control.getAttribute('data-unsaved-ignore') !== null || control.closest?.('[data-unsaved-ignore]')) continue;
    // A control never recorded (not yet seen by the observer) has no baseline, so it is not an edit.
    const initial = initialValues.get(control);
    if (initial !== undefined && !sameValue(control, initial, liveValue(control))) changed.push(control);
  }
  return changed;
}
